/**
 * components/client-detail/DossierPieceList.tsx
 *
 * Sortable list of included DossierPiece items using @dnd-kit.
 * Wraps DossierPieceRow with DndContext and SortableContext for drag-and-drop reordering.
 * Only renders included pieces; excluded/failed pieces are handled by DossierDocSidebar.
 *
 * Updated Apr 2026: replaced onExcludePiece with onExcludeDoc + onPreviewDoc for
 * per-document granularity.
 * Updated Apr 2026: added onReorderDocs and onMoveDoc props for per-file reordering.
 * Updated Jun 2026: added availableFields, onReclassify, onRename props for document
 *   reclassification and renaming within the dossier builder.
 */

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DossierPieceRow from './DossierPieceRow';
import type { DossierPiece, DocumentEntry } from '../../types/dossier';
import type { AvailableField } from './ClientDocumentsTab';

interface DossierPieceListProps {
  includedPieces: DossierPiece[];
  availableFields: AvailableField[];
  onLabelChange: (id: string, label: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onReorder: (orderedIds: string[]) => void;
  onExcludeDoc: (docId: string) => void;
  onPreviewDoc: (doc: DocumentEntry) => void;
  onReorderDocs: (pieceId: string, orderedDocIds: string[]) => void;
  onMoveDoc: (pieceId: string, docId: string, direction: 'up' | 'down') => void;
  onReclassify: (docId: string, targetFieldId: string) => Promise<boolean>;
  onRename: (docId: string, newName: string) => Promise<boolean>;
}

export default function DossierPieceList({
  includedPieces,
  availableFields,
  onLabelChange,
  onMove,
  onReorder,
  onExcludeDoc,
  onPreviewDoc,
  onReorderDocs,
  onMoveDoc,
  onReclassify,
  onRename,
}: DossierPieceListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = includedPieces.findIndex((p) => p.id === active.id);
    const newIndex = includedPieces.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = [...includedPieces];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    onReorder(reordered.map((p) => p.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={includedPieces.map((p) => p.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {includedPieces.map((piece, index) => (
            <DossierPieceRow
              key={piece.id}
              piece={piece}
              index={index}
              total={includedPieces.length}
              availableFields={availableFields}
              onLabelChange={onLabelChange}
              onMove={onMove}
              onExcludeDoc={onExcludeDoc}
              onPreviewDoc={onPreviewDoc}
              onReorderDocs={onReorderDocs}
              onMoveDoc={onMoveDoc}
              onReclassify={onReclassify}
              onRename={onRename}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
